import { useMutation, useQuery } from '@apollo/client/react';
import { type ColumnDef } from '@tanstack/react-table';
import {
  ArrowRightIcon,
  BanIcon,
  CheckIcon,
  FileTextIcon,
  FolderOpenIcon,
  HourglassIcon,
  MoveDiagonalIcon,
  XIcon,
  ZapIcon
} from 'lucide-react';
import { type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { toast } from 'sonner';

import { PaginationControls } from './PaginationControls';
import {
  GENERATE_ICB_REPORT,
  GENERATE_ICM_REPORT,
  GENERATE_RSM_REPORT,
  GET_MY_DEALS,
  UPDATE_OPPORTUNITY
} from './queries';
import type { DealSelects, GetMyDealsData, OpportunityNode } from './types';

import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/Dialog';
import { PdfViewerDialog } from '@/components/reports/PdfViewerDialog';
import { TruncatedTextTooltip } from '@/components/TruncatedTextTooltip';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { DataTable } from '@/components/ui/data-table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle
} from '@/components/ui/empty';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import { Spinner } from '@/components/ui/spinner';
import { Textarea } from '@/components/ui/textarea';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from '@/components/ui/tooltip';
import { formatCurrency } from '@/lib/utils';
import { getInitials } from '@/utils/getInitials';

const ITEMS_PER_PAGE = 20;

const STAGE_SORT_ORDER: Record<string, number> = {
  closing: 0,
  ficm: 2,
  icb: 5,
  icm: 3,
  ioi: 6,
  legal: 1,
  'legal, tax, esg': 1,
  'refined screening': 7,
  screening: 8,
  'term sheet': 4,
  'top of funnel': 9
};

interface UpdateOpportunityResponse {
  updateOpportunity: {
    ok: boolean;
  };
}

interface UpdateOpportunityVariables {
  id: string;
  killedDealNotes?: string;
  killReason?: string;
  status: string;
}

interface GenerateReportResponse {
  generateIcbReport?: {
    data: null | string;
    ok: boolean | null;
  };
  generateIcmReport?: {
    data: null | string;
    ok: boolean | null;
  };
  generateRsmReport?: {
    data: null | string;
    ok: boolean | null;
  };
}

interface MyDealsTabProps {
  dealSelects: DealSelects;
  dealSourceFilter?: string[];
  dealTeamFilter?: string[];
  nameFilter?: string;
  newDealTrigger: ReactNode;
  statusFilter?: string;
}

interface TruncatedBadgeProps {
  children: ReactNode;
  icon?: ReactNode;
  tooltipContent: string;
  variant?: 'green' | 'pastel-slate' | 'red' | 'yellow';
}

const TruncatedBadge = ({
  children,
  icon,
  tooltipContent,
  variant
}: TruncatedBadgeProps) => {
  const [isTruncated, setIsTruncated] = useState(false);
  const textRef = useRef<HTMLSpanElement>(null);

  const handleMouseEnter = () => {
    if (textRef.current) {
      setIsTruncated(textRef.current.scrollWidth > textRef.current.clientWidth);
    }
  };

  return (
    <Tooltip open={isTruncated ? undefined : false}>
      <TooltipTrigger
        asChild
        onMouseEnter={handleMouseEnter}
      >
        <Badge
          className="max-w-full justify-start overflow-hidden"
          variant={variant}
        >
          {icon}
          <span
            className="block max-w-full min-w-0 truncate"
            ref={textRef}
          >
            {children}
          </span>
        </Badge>
      </TooltipTrigger>
      <TooltipContent className="max-w-md">{tooltipContent}</TooltipContent>
    </Tooltip>
  );
};

export function MyDealsTab({
  dealSelects,
  dealSourceFilter,
  dealTeamFilter,
  nameFilter,
  newDealTrigger,
  statusFilter
}: MyDealsTabProps) {
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
  const [killDialogDeal, setKillDialogDeal] = useState<null | OpportunityNode>(
    null
  );
  const [killReason, setKillReason] = useState('');
  const [killNotes, setKillNotes] = useState('');
  const [isReportViewerOpen, setIsReportViewerOpen] = useState(false);
  const [reportViewerData, setReportViewerData] = useState<null | string>(null);
  const [reportViewerFilename, setReportViewerFilename] = useState('');

  const {
    data: myDealsData,
    loading,
    refetch: refetchMyDeals
  } = useQuery<GetMyDealsData>(GET_MY_DEALS, {
    fetchPolicy: 'cache-and-network'
  });

  const onRefresh = useCallback(() => refetchMyDeals(), [refetchMyDeals]);

  const [updateOpportunity, { loading: isUpdating }] = useMutation<
    UpdateOpportunityResponse,
    UpdateOpportunityVariables
  >(UPDATE_OPPORTUNITY);

  const [generateRsmReport, { loading: isGeneratingRsm }] = useMutation<
    GenerateReportResponse,
    { id: string }
  >(GENERATE_RSM_REPORT);
  const [generateIcbReport, { loading: isGeneratingIcb }] = useMutation<
    GenerateReportResponse,
    { id: string }
  >(GENERATE_ICB_REPORT);
  const [generateIcmReport, { loading: isGeneratingIcm }] = useMutation<
    GenerateReportResponse,
    { id: string }
  >(GENERATE_ICM_REPORT);

  const statusNameMap = useMemo(
    () =>
      new Map(
        dealSelects.statusSelections.map(status => [status.value, status.name])
      ),
    [dealSelects.statusSelections]
  );

  const visibleDeals = useMemo(() => {
    let deals = myDealsData?.myDeals.edges.map(edge => edge.node) ?? [];

    if (nameFilter) {
      const lower = nameFilter.toLowerCase();
      deals = deals.filter(d => d.name?.toLowerCase().includes(lower));
    }

    if (statusFilter) {
      deals = deals.filter(d => d.status === statusFilter);
    }

    if (dealTeamFilter && dealTeamFilter.length > 0) {
      deals = deals.filter(d => {
        const md2Id =
          d.md2TeamId != null && typeof d.md2TeamId === 'object'
            ? (d.md2TeamId as { id: string }).id
            : undefined;
        const teamIds = [
          d.smcSource?.id,
          d.mdTeamId?.id,
          md2Id,
          ...(Array.isArray(d.juniorTeamIds)
            ? (d.juniorTeamIds as { id: string }[]).map(m => m.id)
            : []),
          ...(Array.isArray(d.midTeamIds)
            ? (d.midTeamIds as { id: string }[]).map(m => m.id)
            : [])
        ].filter(Boolean);
        return dealTeamFilter.some(id => teamIds.includes(id));
      });
    }

    if (dealSourceFilter && dealSourceFilter.length > 0) {
      deals = deals.filter(
        d => d.dealSource?.id && dealSourceFilter.includes(d.dealSource.id)
      );
    }

    return deals.sort((a, b) => {
      const stageSortDiff = getStageSortKey(a) - getStageSortKey(b);
      if (stageSortDiff !== 0) {
        return stageSortDiff;
      }

      const aHold = a.smPrelHold ?? Number.NEGATIVE_INFINITY;
      const bHold = b.smPrelHold ?? Number.NEGATIVE_INFINITY;
      return bHold - aHold;
    });
  }, [myDealsData, nameFilter, statusFilter, dealTeamFilter, dealSourceFilter]);

  // Reset to page 1 whenever filters change
  const prevFiltersRef = useRef({ dealSourceFilter, dealTeamFilter, nameFilter, statusFilter });
  useEffect(() => {
    const prev = prevFiltersRef.current;
    if (
      prev.nameFilter !== nameFilter ||
      prev.statusFilter !== statusFilter ||
      JSON.stringify(prev.dealTeamFilter) !== JSON.stringify(dealTeamFilter) ||
      JSON.stringify(prev.dealSourceFilter) !== JSON.stringify(dealSourceFilter)
    ) {
      setCurrentPage(1);
      prevFiltersRef.current = { dealSourceFilter, dealTeamFilter, nameFilter, statusFilter };
    }
  }, [nameFilter, statusFilter, dealTeamFilter, dealSourceFilter]);

  const totalDeals = visibleDeals.length;
  const totalPages = Math.max(1, Math.ceil(totalDeals / ITEMS_PER_PAGE));
  const safeCurrentPage = Math.min(currentPage, totalPages);
  const startIndex = (safeCurrentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = Math.min(startIndex + ITEMS_PER_PAGE, totalDeals);
  const pagedDeals = visibleDeals.slice(startIndex, endIndex);

  const getPageNumbers = (): ('ellipsis' | number)[] => {
    const pages: ('ellipsis' | number)[] = [];
    const maxVisiblePages = 5;

    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i += 1) {
        pages.push(i);
      }
      return pages;
    }

    pages.push(1);

    if (safeCurrentPage > 3) {
      pages.push('ellipsis');
    }

    const start = Math.max(2, safeCurrentPage - 1);
    const end = Math.min(totalPages - 1, safeCurrentPage + 1);

    for (let i = start; i <= end; i += 1) {
      pages.push(i);
    }

    if (safeCurrentPage < totalPages - 2) {
      pages.push('ellipsis');
    }

    pages.push(totalPages);
    return pages;
  };

  const handleMoveToActive = async (deal: OpportunityNode) => {
    try {
      await updateOpportunity({
        variables: {
          id: deal.id,
          status: 'active'
        }
      });
      await onRefresh();
      toast.success('Deal moved to active.');
    } catch (error) {
      console.error(error);
      toast.error('Failed to move deal to active.');
    }
  };

  const handleKillDeal = async () => {
    if (!killDialogDeal || !killReason) {
      return;
    }

    try {
      await updateOpportunity({
        variables: {
          id: killDialogDeal.id,
          killedDealNotes: killNotes || undefined,
          killReason,
          status: 'killed'
        }
      });
      await onRefresh();
      toast.success('Deal has been killed.');
      setKillDialogDeal(null);
      setKillReason('');
      setKillNotes('');
    } catch (error) {
      console.error(error);
      toast.error('Failed to kill deal.');
    }
  };

  const handleOpenReport = async (deal: OpportunityNode) => {
    const reportKind = getReportKind(
      deal.dueDiligenceProjectId?.nextMilestoneName
    );
    if (!reportKind) {
      return;
    }

    try {
      const response =
        reportKind === 'rsm'
          ? await generateRsmReport({ variables: { id: deal.id } })
          : reportKind === 'icb'
            ? await generateIcbReport({ variables: { id: deal.id } })
            : await generateIcmReport({ variables: { id: deal.id } });

      const base64 =
        reportKind === 'rsm'
          ? response.data?.generateRsmReport?.data
          : reportKind === 'icb'
            ? response.data?.generateIcbReport?.data
            : response.data?.generateIcmReport?.data;

      if (!base64) {
        toast.error('Report is not available.');
        return;
      }

      const filename =
        reportKind === 'rsm'
          ? `${deal.name || 'deal'}-rsm-report.pdf`
          : reportKind === 'icb'
            ? `${deal.name || 'deal'}-icb-report.pdf`
            : `${deal.name || 'deal'}-icm-report.pdf`;

      setReportViewerData(base64);
      setReportViewerFilename(filename);
      setIsReportViewerOpen(true);
    } catch (error) {
      console.error(error);
      toast.error('Failed to open report.');
    }
  };

  const columns = useMemo<ColumnDef<OpportunityNode>[]>(
    () => [
      {
        cell: ({ row }) => (
          <div className="bg-background flex h-full w-full items-center justify-center">
            <Button
              asChild
              size="icon"
              variant="ghost"
            >
              <Link to={`/investments/direct/${row.original.id}/overview`}>
                <MoveDiagonalIcon />
              </Link>
            </Button>
          </div>
        ),
        enableSorting: false,
        header: () => <div className="bg-background h-full w-full" />,
        id: 'openDeal',
        meta: {
          cellClassName: 'p-0 bg-background',
          headerClassName: 'px-0 bg-background'
        },
        size: 52
      },
      {
        cell: ({ row }) => {
          const deal = row.original;
          const isTopOfFunnel = deal.stage === 'top_of_funnel';
          const isActive = deal.stage === 'active';
          const reportKind = getReportKind(
            deal.dueDiligenceProjectId?.nextMilestoneName
          );

          return (
            <div className="bg-background flex h-full w-full items-center justify-center">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    onClick={event => {
                      event.stopPropagation();
                    }}
                    size="icon"
                    variant="ghost"
                  >
                    <ZapIcon />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  {isActive && (
                    <DropdownMenuItem
                      onClick={event => {
                        event.stopPropagation();
                        navigate(
                          `/investments/direct/${deal.id}/due-diligence`
                        );
                      }}
                    >
                      <ArrowRightIcon className="size-4" />
                      Proceed to DD
                    </DropdownMenuItem>
                  )}

                  {isTopOfFunnel && (
                    <DropdownMenuItem
                      onClick={event => {
                        event.stopPropagation();
                        void handleMoveToActive(deal);
                      }}
                    >
                      <CheckIcon className="size-4" />
                      Move to Active
                    </DropdownMenuItem>
                  )}

                  {isActive && reportKind && (
                    <DropdownMenuItem
                      onClick={event => {
                        event.stopPropagation();
                        void handleOpenReport(deal);
                      }}
                    >
                      <FileTextIcon className="size-4" />
                      {reportKind === 'rsm'
                        ? 'Open RSM'
                        : reportKind === 'icb'
                          ? 'Open ICB'
                          : 'Open ICM'}
                    </DropdownMenuItem>
                  )}

                  {(isTopOfFunnel || isActive) && (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        onClick={event => {
                          event.stopPropagation();
                          setKillDialogDeal(deal);
                          setKillReason('');
                          setKillNotes('');
                        }}
                        variant="destructive"
                      >
                        <BanIcon className="size-4" />
                        Kill
                      </DropdownMenuItem>
                    </>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          );
        },
        enableSorting: false,
        header: () => <div className="bg-background h-full w-full" />,
        id: 'actions',
        meta: {
          cellClassName: 'p-0 bg-background',
          headerClassName: 'px-0 bg-background'
        },
        size: 52
      },
      {
        accessorKey: 'name',
        cell: ({ row }) => (
          <div className="font-medium">
            <TruncatedTextTooltip>
              {row.original.name || '-'}
            </TruncatedTextTooltip>
          </div>
        ),
        enableSorting: false,
        header: () => (
          <div className="w-[150px] text-xs font-semibold">Deal Name</div>
        ),
        meta: {
          cellClassName: 'w-[150px] max-w-[150px]',
          headerClassName: 'w-[150px] max-w-[150px]'
        }
      },
      {
        cell: ({ row }) => (
          <TruncatedBadge
            tooltipContent={getStageLabel(row.original)}
            variant="pastel-slate"
          >
            {getStageLabel(row.original)}
          </TruncatedBadge>
        ),
        enableSorting: false,
        header: () => (
          <div className="w-[120px] text-xs font-semibold">Stage</div>
        ),
        id: 'stageLabel'
      },
      {
        accessorKey: 'status',
        cell: ({ row }) => {
          const status = row.original.status;
          if (!status) {
            return '-';
          }

          const label = statusNameMap.get(status) ?? status;

          if (status === 'long_term') {
            return (
              <TruncatedBadge
                icon={<HourglassIcon />}
                tooltipContent="Long Term"
                variant="pastel-slate"
              >
                Long Term
              </TruncatedBadge>
            );
          }

          if (status === 'killed') {
            return (
              <TruncatedBadge
                icon={<XIcon />}
                tooltipContent="Killed"
                variant="red"
              >
                Killed
              </TruncatedBadge>
            );
          }

          if (
            status === 'not_enough_information' ||
            status === 'new_unqualified'
          ) {
            return (
              <TruncatedBadge
                icon={<XIcon />}
                tooltipContent={label}
                variant="red"
              >
                {label}
              </TruncatedBadge>
            );
          }

          if (status === 'closed') {
            return (
              <TruncatedBadge
                icon={<CheckIcon />}
                tooltipContent="Closed"
                variant="green"
              >
                Closed
              </TruncatedBadge>
            );
          }

          return (
            <TruncatedBadge
              icon={<CheckIcon />}
              tooltipContent={label}
              variant="green"
            >
              {label}
            </TruncatedBadge>
          );
        },
        enableSorting: false,
        header: () => (
          <div className="w-[130px] text-xs font-semibold">Status</div>
        )
      },
      {
        cell: ({ row }) =>
          row.original.dueDiligenceProjectId?.nextMilestoneName ? (
            <TruncatedBadge
              tooltipContent={
                row.original.dueDiligenceProjectId.nextMilestoneName
              }
              variant="pastel-slate"
            >
              {row.original.dueDiligenceProjectId.nextMilestoneName}
            </TruncatedBadge>
          ) : (
            '-'
          ),
        enableSorting: false,
        header: () => (
          <div className="w-[130px] text-xs font-semibold">DD Phase</div>
        ),
        id: 'ddPhase'
      },
      {
        accessorKey: 'readableSmcIndustry',
        cell: ({ row }) =>
          row.original.readableSmcIndustry ? (
            <TruncatedBadge
              tooltipContent={row.original.readableSmcIndustry}
              variant="pastel-slate"
            >
              {row.original.readableSmcIndustry}
            </TruncatedBadge>
          ) : (
            '-'
          ),
        enableSorting: false,
        header: () => (
          <div className="w-[140px] text-xs font-semibold">Industry</div>
        )
      },
      {
        cell: ({ row }) =>
          row.original.smcSource?.name ? (
            <div className="flex items-center gap-x-2 overflow-hidden">
              <Avatar className="shrink-0">
                <AvatarFallback>
                  {getInitials(row.original.smcSource.name)}
                </AvatarFallback>
              </Avatar>
              <TruncatedTextTooltip>
                {row.original.smcSource.name}
              </TruncatedTextTooltip>
            </div>
          ) : (
            '-'
          ),
        enableSorting: false,
        header: () => (
          <div className="w-[130px] text-xs font-semibold">SMC Source</div>
        ),
        id: 'smcSource'
      },
      {
        cell: ({ row }) =>
          row.original.dealSource?.name ? (
            <div className="flex items-center gap-x-2 overflow-hidden">
              <Avatar className="shrink-0">
                <AvatarFallback>
                  {getInitials(row.original.dealSource.name)}
                </AvatarFallback>
              </Avatar>
              <TruncatedTextTooltip>
                {row.original.dealSource.name}
              </TruncatedTextTooltip>
            </div>
          ) : (
            '-'
          ),
        enableSorting: false,
        header: () => (
          <div className="w-[130px] text-xs font-semibold">Deal Source</div>
        ),
        id: 'dealSource'
      },
      {
        accessorKey: 'ltmRevenue',
        cell: ({ row }) => (
          <div className="text-right">
            {formatCurrency(row.original.ltmRevenue, { showNullAs: '-' })}
          </div>
        ),
        enableSorting: false,
        header: () => (
          <div className="w-[130px] text-xs font-semibold">Revenue</div>
        ),
        meta: {
          cellClassName: 'w-[130px] max-w-[130px]',
          headerClassName: 'w-[130px] max-w-[130px]'
        }
      },
      {
        accessorKey: 'ltmEbitda',
        cell: ({ row }) => (
          <div className="text-right">
            {formatCurrency(row.original.ltmEbitda, { showNullAs: '-' })}
          </div>
        ),
        enableSorting: false,
        header: () => (
          <div className="w-[130px] text-xs font-semibold">EBITDA</div>
        ),
        meta: {
          cellClassName: 'w-[130px] max-w-[130px]',
          headerClassName: 'w-[130px] max-w-[130px]'
        }
      },
      {
        accessorKey: 'smPrelHold',
        cell: ({ row }) =>
          formatCurrency(row.original.smPrelHold, { showNullAs: '-' }),
        enableSorting: false,
        header: () => (
          <div className="w-[150px] text-right text-xs font-semibold">
            SM Investment Hold
          </div>
        ),
        meta: {
          cellClassName: 'text-right w-[150px] max-w-[150px]',
          headerClassName: 'w-[150px] max-w-[150px]'
        }
      },
      {
        accessorKey: 'dueDate',
        cell: ({ row }) => (
          <TruncatedTextTooltip>
            {formatDate(row.original.ioiDueDate)}
          </TruncatedTextTooltip>
        ),
        enableSorting: false,
        header: () => (
          <div className="w-[110px] text-xs font-semibold">Due Date</div>
        )
      },
      {
        accessorKey: 'ioiTarget',
        cell: ({ row }) => (
          <TruncatedTextTooltip>
            {formatDate(row.original.ioiDueDate)}
          </TruncatedTextTooltip>
        ),
        enableSorting: false,
        header: () => (
          <div className="w-[110px] text-xs font-semibold">IOI Target</div>
        )
      },
      {
        accessorKey: 'bestEstimateDate',
        cell: ({ row }) => (
          <TruncatedTextTooltip>
            {formatDate(row.original.bestEstimateDate)}
          </TruncatedTextTooltip>
        ),
        enableSorting: false,
        header: () => (
          <div className="w-[140px] text-xs font-semibold">Closing Target</div>
        )
      }
    ],
    [navigate, statusNameMap]
  );

  return (
    <>
      <div className="grid h-full max-h-full grid-rows-[1fr_auto]">
        {loading || !myDealsData?.myDeals ? (
          <div className="flex min-h-[60vh] w-full items-center justify-center">
            <Spinner className="size-8 text-blue-500" />
          </div>
        ) : visibleDeals.length === 0 ? (
          <Empty className="pt-52!">
            <EmptyHeader>
              <EmptyMedia variant="icon">
                <FolderOpenIcon />
              </EmptyMedia>
              <EmptyTitle>No deals yet</EmptyTitle>
              <EmptyDescription>
                You haven't been assigned to any deals yet. Get started by
                adding your first deal.
              </EmptyDescription>
            </EmptyHeader>
            <EmptyContent>{newDealTrigger}</EmptyContent>
          </Empty>
        ) : (
          <DataTable
            className="min-w-max table-fixed"
            columns={columns}
            data={pagedDeals}
            enableColumnResizing
            getRowId={row => row.id}
            initialColumnPinning={{ left: ['openDeal', 'actions', 'name'] }}
            manualSorting={false}
            persistColumnSizingKey="direct-underwriting-my-deals-table"
          />
        )}

        <PaginationControls
          currentPage={safeCurrentPage}
          endIndex={endIndex}
          getPageNumbers={getPageNumbers}
          itemLabel="deals"
          itemsPerPage={ITEMS_PER_PAGE}
          onNextPage={() => {
            if (safeCurrentPage < totalPages) {
              setCurrentPage(safeCurrentPage + 1);
            }
          }}
          onPageClick={setCurrentPage}
          onPreviousPage={() => {
            if (safeCurrentPage > 1) {
              setCurrentPage(safeCurrentPage - 1);
            }
          }}
          startIndex={startIndex}
          totalItems={totalDeals}
          totalPages={totalPages}
        />
      </div>

      <Dialog
        onOpenChange={open => {
          if (!open) {
            setKillDialogDeal(null);
            setKillReason('');
            setKillNotes('');
          }
        }}
        open={Boolean(killDialogDeal)}
      >
        <DialogContent aria-describedby={undefined}>
          <DialogHeader>
            <DialogTitle>Kill Deal</DialogTitle>
          </DialogHeader>

          <DialogBody>
            <div className="space-y-4">
              <Select
                onValueChange={setKillReason}
                value={killReason}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select reason" />
                </SelectTrigger>
                <SelectContent>
                  {dealSelects.killReasonSelections.map(reason => (
                    <SelectItem
                      key={reason.id}
                      value={reason.value}
                    >
                      {reason.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Textarea
                onChange={event => {
                  setKillNotes(event.target.value);
                }}
                placeholder="Notes"
                value={killNotes}
              />
            </div>
          </DialogBody>

          <DialogFooter>
            <Button
              onClick={() => {
                setKillDialogDeal(null);
                setKillReason('');
                setKillNotes('');
              }}
              variant="outline"
            >
              Cancel
            </Button>
            <Button
              disabled={!killReason || isUpdating}
              onClick={() => {
                void handleKillDeal();
              }}
              variant="destructive"
            >
              {isUpdating ? 'Killing...' : 'Confirm Kill'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {(isGeneratingRsm || isGeneratingIcb || isGeneratingIcm) && (
        <div className="bg-background pointer-events-none fixed right-5 bottom-5 z-50 rounded-md border px-3 py-2 text-sm shadow-sm">
          Generating report...
        </div>
      )}

      <PdfViewerDialog
        description="View report content"
        filename={reportViewerFilename}
        onOpenChange={open => {
          setIsReportViewerOpen(open);
          if (!open) {
            setReportViewerData(null);
            setReportViewerFilename('');
          }
        }}
        open={isReportViewerOpen}
        pdfData={reportViewerData}
        title="Report Viewer"
      />
    </>
  );
}

function formatDate(value: null | string | undefined): string {
  return value ? value.slice(0, 10) : '-';
}

function getReportKind(
  phase: null | string | undefined
): 'icb' | 'icm' | 'rsm' | null {
  const normalizedPhase = normalizePhase(phase);

  if (normalizedPhase === 'icb') {
    return 'icb';
  }

  if (normalizedPhase === 'icm' || normalizedPhase === 'ficm') {
    return 'icm';
  }

  if (
    normalizedPhase === 'rsm' ||
    normalizedPhase === 'refined screening' ||
    normalizedPhase === 'screening'
  ) {
    return 'rsm';
  }

  return null;
}

function getStageLabel(deal: OpportunityNode): string {
  return deal.stage === 'active' ? 'Active' : 'Top of Funnel';
}

function getStageSortKey(deal: OpportunityNode): number {
  if (deal.stage === 'top_of_funnel') {
    return STAGE_SORT_ORDER['top of funnel'];
  }

  const phase = normalizePhase(deal.dueDiligenceProjectId?.nextMilestoneName);
  return STAGE_SORT_ORDER[phase] ?? Number.MAX_SAFE_INTEGER;
}

function normalizePhase(phase: null | string | undefined): string {
  return (phase ?? '').trim().toLowerCase();
}
